import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { EPIC_SLUGS } from "../../../constants/epicLeaderboard";
import type { RangeOption } from "../../../constants/epicLeaderboard";
import { ApiError, useApiResource } from "../../../hooks/useApiResource";
import type { ApiResource } from "../../../hooks/useApiResource";
import type {
  PlayerEpicStat,
  PlayerEpicStatsResponse
} from "../../../types/epicLeaderboard";
import type { PkInsights } from "../../../types/playerPk";
import { playerApiUrl } from "../../../utils/playerApi";
import {
  fetchPlayerEpicStats,
  peekPlayerEpicStats
} from "../../../utils/playerEpicApi";
import PlayerLink from "../../common/PlayerLink";
import HelpTip from "../../common/HelpTip";
import SkeletonBlock from "../../common/SkeletonBlock";
import type { PlayerStats } from "../PlayerOverviewTab";
import { PkError } from "./PkSection";
import { formatRatio } from "./PkStatTile";
import "../../epicLeaderboard/epicLeaderboard.css";
import "./pk.css";

// Fewer fights than this and a ratio says more about luck than skill.
const MIN_FIGHTS = 5;
const SMALL_SAMPLE_TIP = "Too few fights in this range to compare.";

const EPIC_SHORT_NAMES: Record<string, string> = {
  "Sea King's Dominion": "SKD",
  "Titans' Keep": "Titans' Keep",
  "The Cracks of Terra": "Terra"
};

// Epic stats for one player + range, through the cache the Overview and Epics
// tabs share. Keeps the previous range's data (same player) while a new one
// loads.
const usePlayerEpics = (
  player: string,
  startDate: string | undefined
): ApiResource<PlayerEpicStatsResponse> => {
  const scope = player.toLowerCase();
  const key = `${scope}|${startDate ?? ""}`;
  const [state, setState] = useState<{
    scope: string;
    data: PlayerEpicStatsResponse | null;
    doneKey: string | null;
    error: ApiError | null;
  }>({ scope, data: null, doneKey: null, error: null });
  const [reloadCount, setReloadCount] = useState(0);
  // Last data shown, including responses that came straight from the cache.
  const last = useRef<{
    scope: string;
    data: PlayerEpicStatsResponse;
  } | null>(null);

  const cached = peekPlayerEpicStats(player, startDate);
  if (cached) last.current = { scope, data: cached };

  useEffect(() => {
    if (peekPlayerEpicStats(player, startDate)) return undefined;
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await fetchPlayerEpicStats(
          player,
          startDate,
          controller.signal
        );
        if (controller.signal.aborted) return;
        setState({ scope, data, doneKey: key, error: null });
      } catch (err) {
        if (controller.signal.aborted || axios.isCancel(err)) return;
        console.error("Failed to fetch epic stats", player, err);
        setState((prev) => ({
          ...prev,
          doneKey: key,
          error: new ApiError(0, "Couldn't reach the server")
        }));
      }
    };
    load();
    return () => controller.abort();
  }, [player, startDate, scope, key, reloadCount]);

  const reload = () => {
    setState((prev) => ({ ...prev, doneKey: null, error: null }));
    setReloadCount((n) => n + 1);
  };

  if (cached) return { data: cached, loading: false, error: null, reload };
  const settled = state.doneKey === key;
  if (settled && state.data && state.scope === scope) {
    last.current = { scope, data: state.data };
  }
  return {
    data: last.current?.scope === scope ? last.current.data : null,
    loading: !settled,
    error: settled ? state.error : null,
    reload
  };
};

type Cell = {
  text: string;
  // Counts behind the value, shown muted under it.
  sub?: string;
  // What gets compared; null when this side has nothing to compare.
  value: number | null;
};

type Row = {
  key: string;
  label: string;
  tooltip?: string;
  cells: [Cell, Cell];
  lowerIsBetter?: boolean;
  // Ratio rows on a handful of fights: shown, but not judged.
  smallSample?: boolean;
};

type Side = {
  insights: PkInsights | null;
  stats: PlayerStats | null;
  epics: PlayerEpicStat[];
};

const NONE: Cell = { text: "-", value: null };

const countCell = (n: number | undefined): Cell =>
  n === undefined ? NONE : { text: n.toLocaleString(), value: n };

const rankCell = (stats: PlayerStats | null, type: string): Cell => {
  const n = Number(stats?.ranks?.find((e) => e.type === type)?.row_num);
  return Number.isFinite(n) && n > 0 ? { text: `#${n}`, value: n } : NONE;
};

const buildRows = (a: Side, b: Side): Row[] => {
  const pair = (f: (side: Side) => Cell): [Cell, Cell] => [f(a), f(b)];
  const lacksFights = (side: Side) =>
    !!side.insights &&
    side.insights.summary.kills + side.insights.summary.deaths < MIN_FIGHTS;
  const lacksKills = (side: Side) =>
    !!side.insights && side.insights.summary.kills < MIN_FIGHTS;
  const bothHaveData = !!a.insights && !!b.insights;

  const rows: Row[] = [
    {
      key: "kills",
      label: "Kills",
      cells: pair((s) => countCell(s.insights?.summary.kills))
    },
    {
      key: "deaths",
      label: "Deaths",
      lowerIsBetter: true,
      cells: pair((s) => countCell(s.insights?.summary.deaths))
    },
    {
      key: "assists",
      label: "Assists",
      cells: pair((s) => countCell(s.insights?.summary.assists))
    },
    {
      key: "kd",
      label: "K/D",
      tooltip: "Kills per death.",
      smallSample: bothHaveData && (lacksFights(a) || lacksFights(b)),
      cells: pair((s) => {
        const sm = s.insights?.summary;
        if (!sm) return NONE;
        return {
          text: formatRatio(sm.kdRatio),
          sub: `${sm.kills.toLocaleString()} / ${sm.deaths.toLocaleString()}`,
          value: sm.kdRatio
        };
      })
    },
    {
      key: "participation",
      label: "Kill participation",
      tooltip: "(Kills + assists) per death.",
      smallSample: bothHaveData && (lacksFights(a) || lacksFights(b)),
      cells: pair((s) => {
        const sm = s.insights?.summary;
        if (!sm) return NONE;
        return {
          text: formatRatio(sm.killParticipation),
          sub: `${(sm.kills + sm.assists).toLocaleString()} / ${sm.deaths.toLocaleString()}`,
          value: sm.killParticipation
        };
      })
    },
    {
      key: "solo",
      label: "Solo kills %",
      tooltip: "Share of kills made without an assister.",
      smallSample: bothHaveData && (lacksKills(a) || lacksKills(b)),
      cells: pair((s) => {
        const sm = s.insights?.summary;
        if (!sm) return NONE;
        return {
          text: sm.soloPct === null ? "-" : `${sm.soloPct}%`,
          sub: `${sm.soloKills.toLocaleString()} of ${sm.kills.toLocaleString()}`,
          value: sm.soloPct
        };
      })
    },
    {
      key: "rank-points",
      label: "Points rank",
      lowerIsBetter: true,
      cells: pair((s) => rankCell(s.stats, "points"))
    },
    {
      key: "rank-pk",
      label: "PK rank",
      lowerIsBetter: true,
      cells: pair((s) => rankCell(s.stats, "pk"))
    },
    {
      key: "rank-hc",
      label: "HC rank",
      lowerIsBetter: true,
      cells: pair((s) => rankCell(s.stats, "hc"))
    },
    {
      key: "rank-assists",
      label: "Assist rank",
      lowerIsBetter: true,
      cells: pair((s) => rankCell(s.stats, "assists"))
    },
    {
      key: "streak",
      label: "Best kill streak",
      tooltip: "Longest run of kills without dying in this range.",
      cells: pair((s) => {
        if (!s.insights) return NONE;
        return countCell(s.insights.streaks.bestKill?.length ?? 0);
      })
    },
    {
      key: "hc-kills",
      label: "HC kills",
      cells: pair((s) => countCell(s.insights?.hc.kills))
    }
  ];

  Object.values(EPIC_SLUGS).forEach((epic) => {
    const [statA, statB] = [a, b].map((s) =>
      s.epics.find((e) => e.epic === epic)
    );
    if (!statA && !statB) return;
    const toCell = (stat: PlayerEpicStat | undefined): Cell =>
      stat ? { text: stat.fastest, value: stat.fastestSeconds } : NONE;
    rows.push({
      key: `epic-${epic}`,
      label: `Fastest ${EPIC_SHORT_NAMES[epic] ?? epic}`,
      tooltip: `Fastest ${epic} clear in this range.`,
      lowerIsBetter: true,
      cells: [toCell(statA), toCell(statB)]
    });
  });

  return rows;
};

// 0 or 1 for the better side, -1 for no highlight.
const betterSide = (row: Row): number => {
  if (row.smallSample) return -1;
  const [x, y] = row.cells;
  if (x.value === null || y.value === null || x.value === y.value) return -1;
  const firstWins = row.lowerIsBetter ? x.value < y.value : x.value > y.value;
  return firstWins ? 0 : 1;
};

const SideBySideSkeleton = () => (
  <div
    className="pk-card"
    role="status"
    aria-label="Loading side-by-side comparison"
  >
    <div className="pk-subtitle">Side by side</div>
    {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
      <SkeletonBlock
        key={i}
        height={26}
        className="mb-2"
        style={{ borderRadius: 6 }}
      />
    ))}
  </div>
);

type Props = {
  player: string;
  opponent: string;
  range: RangeOption;
  // ISO start of the range, undefined for all time.
  startDate: string | undefined;
  // Already loaded by the PK tab; passed in so it isn't requested twice.
  viewerInsights: ApiResource<PkInsights>;
  // The surrounding section is already dimmed while it refetches.
  parentRefetching: boolean;
};

const SideBySide = ({
  player,
  opponent,
  range,
  startDate,
  viewerInsights,
  parentRefetching
}: Props) => {
  const viewerScope = player.toLowerCase();
  const opponentScope = opponent.toLowerCase();

  // The viewer's base stats and epics share cache keys with the Overview and
  // Epics tabs; the opponent's three requests start together.
  const viewerStats = useApiResource<PlayerStats>(
    useMemo(
      () => playerApiUrl(player, "", { startDate }),
      [player, startDate]
    ),
    viewerScope
  );
  const viewerEpics = usePlayerEpics(player, startDate);
  const opponentInsights = useApiResource<PkInsights>(
    useMemo(
      () => playerApiUrl(opponent, "pk-insights", { startDate }),
      [opponent, startDate]
    ),
    opponentScope
  );
  const opponentStats = useApiResource<PlayerStats>(
    useMemo(
      () => playerApiUrl(opponent, "", { startDate }),
      [opponent, startDate]
    ),
    opponentScope
  );
  const opponentEpics = usePlayerEpics(opponent, startDate);

  // 404 = the opponent has no PK records: their column is just empty.
  const opponentHasNoPk = opponentInsights.error?.status === 404;
  const resources: ApiResource<unknown>[] = [
    viewerInsights,
    viewerStats,
    viewerEpics,
    opponentInsights,
    opponentStats,
    opponentEpics
  ];
  const failed = resources.filter(
    (r) => r.error && !(r === opponentInsights && opponentHasNoPk)
  );

  if (failed.length > 0) {
    return (
      <PkError
        what="the side-by-side comparison"
        error={failed[0].error}
        onRetry={() => failed.forEach((r) => r.reload())}
      />
    );
  }

  if (
    !viewerInsights.data ||
    !viewerStats.data ||
    !viewerEpics.data ||
    !opponentStats.data ||
    !opponentEpics.data ||
    !(opponentInsights.data || opponentHasNoPk)
  ) {
    return <SideBySideSkeleton />;
  }

  const refetching = resources.some((r) => r.loading && !!r.data);
  const rows = buildRows(
    {
      insights: viewerInsights.data,
      stats: viewerStats.data,
      epics: viewerEpics.data.epics
    },
    {
      insights: opponentHasNoPk ? null : opponentInsights.data,
      stats: opponentStats.data,
      epics: opponentEpics.data.epics
    }
  );

  return (
    <div
      className={
        refetching && !parentRefetching ? "pk-sbs is-refetching" : "pk-sbs"
      }
    >
      <div className="pk-card">
        <div className="pk-subtitle">Side by side</div>
        <div className="epic-lb-table-scroll">
          <table className="epic-lb-table pk-sbs-table">
            <thead>
              <tr>
                <th>
                  <span className="visually-hidden">Stat</span>
                </th>
                <th className="pk-num pk-sbs-name">{player}</th>
                <th className="pk-num pk-sbs-name">
                  <PlayerLink className="pk-link" player={opponent} range={range} tab="pk" />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const best = betterSide(row);
                return (
                  <tr
                    key={row.key}
                    className={row.smallSample ? "pk-sbs-faded" : undefined}
                  >
                    <td className="pk-sbs-label">
                      {row.label}
                      {row.tooltip && !row.smallSample && (
                        <HelpTip text={row.tooltip} />
                      )}
                      {row.smallSample && <HelpTip text={SMALL_SAMPLE_TIP} />}
                    </td>
                    {row.cells.map((cell, i) => (
                      <td
                        key={i}
                        className={
                          best === i ? "pk-num pk-sbs-best" : "pk-num"
                        }
                      >
                        <div>{cell.text}</div>
                        {cell.sub && (
                          <div className="epic-lb-muted">{cell.sub}</div>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {opponentHasNoPk && (
          <div className="epic-lb-muted mt-2">
            {opponent} has no PK records, so there's nothing to compare their
            PK stats with.
          </div>
        )}
      </div>
    </div>
  );
};

export default SideBySide;
